import type {
  ActivityCategory,
  Audience,
  CategoryStyle,
  EmptyStateCopy,
  EmptyStateCta,
  FeedFilter
} from './types';

export const PROFESSIONAL_FILTERS: FeedFilter[] = [
  { id: 'all', label: 'All', group: null },
  { id: 'applications', label: 'Applications', group: 'applications' },
  { id: 'jobs', label: 'Jobs', group: 'jobs' },
  { id: 'messages', label: 'Messages', group: 'messages' },
  { id: 'temp', label: 'Temp', group: 'temp' }
];

export const PRACTICE_FILTERS: FeedFilter[] = [
  { id: 'all', label: 'All', group: null },
  { id: 'applicants', label: 'Applicants', group: 'applications' },
  { id: 'jobs', label: 'Jobs', group: 'jobs' },
  { id: 'messages', label: 'Messages', group: 'messages' },
  { id: 'temp', label: 'Temp', group: 'temp' }
];

// Icon and color for each row category. Tones reuse the dashboard card
// palette: primary blue, light blue (info), green (success), orange
// (warning), purple, and gray (muted) for everything else.
export const CATEGORY_STYLES: Record<ActivityCategory, CategoryStyle> = {
  applicants: { icon: 'fa-users', tone: 'info' },
  jobs: { icon: 'fa-briefcase', tone: 'primary' },
  messages: { icon: 'fa-comment', tone: 'success' },
  temp: { icon: 'fa-calendar-alt', tone: 'purple' },
  invites: { icon: 'fa-paper-plane', tone: 'warning' },
  favorites: { icon: 'fa-heart', tone: 'warning' },
  team: { icon: 'fa-user-plus', tone: 'muted' },
  profile: { icon: 'fa-user-edit', tone: 'muted' },
  matches: { icon: 'fa-user-check', tone: 'info' },
  other: { icon: 'fa-bell', tone: 'muted' }
};

// Keyed by the part of the kind before the first dot, so
// "job_application.created" and "job_application.withdrawn" share a bucket.
const KIND_PREFIX_CATEGORY: Record<string, ActivityCategory> = {
  job_application: 'applicants',
  job: 'jobs',
  jobs: 'jobs',
  message: 'messages',
  temp_request: 'temp',
  temp_rating: 'temp',
  application_invite: 'invites',
  auto_invites: 'invites',
  favorite_practice: 'favorites',
  team: 'team',
  profile: 'profile',
  candidate_matches: 'matches'
};

export const categoryFor = (kind: string | null | undefined): ActivityCategory => {
  const prefix = (kind || '').split('.')[0];
  return Object.prototype.hasOwnProperty.call(KIND_PREFIX_CATEGORY, prefix)
    ? KIND_PREFIX_CATEGORY[prefix]
    : 'other';
};

// A professional's application rows are about their own applications, not a
// group of applicants, so they get a document glyph in the same tone.
const PROFESSIONAL_OVERRIDES: Partial<Record<ActivityCategory, CategoryStyle>> = {
  applicants: { icon: 'fa-file-alt', tone: 'info' }
};

export const categoryStyleFor = (kind: string | null | undefined, audience?: Audience | null): CategoryStyle => {
  const category = categoryFor(kind);
  if (audience === 'professional' && PROFESSIONAL_OVERRIDES[category]) {
    return PROFESSIONAL_OVERRIDES[category] as CategoryStyle;
  }
  return CATEGORY_STYLES[category];
};

const PROFESSIONAL_EMPTY: Record<string, EmptyStateCopy> = {
  all: {
    title: 'No activity yet',
    body: 'Complete your profile so practices can find you.'
  },
  applications: {
    title: 'No application activity',
    body: 'Search for jobs or check your invites.'
  },
  jobs: {
    title: 'No job activity',
    body: 'Search for jobs that match your profile.'
  },
  messages: {
    title: 'No messages yet',
    body: 'When a practice messages you, it will show up here.'
  },
  temp: {
    title: 'No temp activity',
    body: 'Set your availability to get temp requests.'
  }
};

const PRACTICE_EMPTY: Record<string, EmptyStateCopy> = {
  all: {
    title: 'No activity yet',
    body: 'Post a job to start seeing applicants and matches.'
  },
  applicants: {
    title: 'No applicant activity',
    body: 'Invite candidates or wait for new applications.'
  },
  jobs: {
    title: 'No job activity',
    body: 'Post a job to start hiring.'
  },
  messages: {
    title: 'No messages yet',
    body: 'When a professional messages you, it will show up here.'
  },
  temp: {
    title: 'No temp activity',
    body: 'Set up a temp request to fill a shift.'
  }
};

// Icon badge shown on the empty state for each chip.
const EMPTY_CATEGORY: Record<string, ActivityCategory> = {
  applications: 'applicants',
  applicants: 'applicants',
  jobs: 'jobs',
  messages: 'messages',
  temp: 'temp'
};

export const DEFAULT_EMPTY_CTA_HREF: Record<Audience, string> = {
  practice: '/jobs/new',
  professional: '/jobs'
};

export const filtersFor = (audience: Audience): FeedFilter[] =>
  audience === 'professional' ? PROFESSIONAL_FILTERS : PRACTICE_FILTERS;

export const DEFAULT_TEMP_CTA_HREF: Record<Audience, string> = {
  practice: '/schedule',
  professional: '/availability'
};

// Shown as a muted line under the feed header for multi-location practices.
export const MULTI_LOCATION_NOTE = 'Showing activity across all your locations.';

export const emptyStateFor = (audience: Audience, filterId: string): EmptyStateCopy => {
  const table = audience === 'professional' ? PROFESSIONAL_EMPTY : PRACTICE_EMPTY;
  return table[filterId] || table.all;
};

export const emptyStateStyleFor = (filterId: string): CategoryStyle => {
  const category = EMPTY_CATEGORY[filterId];
  return category ? CATEGORY_STYLES[category] : { icon: 'fa-bell', tone: 'primary' };
};

// One button per empty state. The Temp chip points at the temp tools so the
// button matches the copy; every other chip uses the audience default.
export const emptyStateCtaFor = (
  audience: Audience,
  href?: string | null,
  filterId = 'all',
  tempHref?: string | null
): EmptyStateCta => {
  if (filterId === 'temp') {
    return audience === 'professional'
      ? { label: 'Update availability', href: tempHref || DEFAULT_TEMP_CTA_HREF.professional }
      : { label: 'Schedule a temp', href: tempHref || DEFAULT_TEMP_CTA_HREF.practice };
  }
  return audience === 'professional'
    ? { label: 'Search jobs', href: href || DEFAULT_EMPTY_CTA_HREF.professional }
    : { label: 'Post a job', href: href || DEFAULT_EMPTY_CTA_HREF.practice };
};
